import React, { useState, useEffect, useCallback } from 'react';
import pdfFile from './assets/Saraswathi-26.pdf';

const DOC_EYEBROW = 'Annual General Meeting';
const DOC_TITLE = 'AGM Report';
const DOC_YEAR = '2025\u201326';
const DOWNLOAD_NAME = 'AGM 2025-26.pdf';

const styles = `
.agm-root {
  --ink: #14212B;
  --slate: #4A5C68;
  --line: #DDE3E7;
  --surface: #FFFFFF;
  --page: #EDF1F3;
  --accent: #1E6F5C;

  min-height: 100vh;
  background: var(--page);
  padding: 48px 20px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  color: var(--ink);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  box-sizing: border-box;
}
.agm-root *, .agm-root *::before, .agm-root *::after { box-sizing: border-box; }

.agm-card {
  width: 100%;
  max-width: 640px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-left: 3px solid var(--accent);
  border-radius: 2px;
  padding: 40px 40px 32px;
  box-shadow: 0 1px 2px rgba(20, 33, 43, .06), 0 12px 32px -18px rgba(20, 33, 43, .35);
  position: relative;
}

.agm-stamp {
  position: absolute;
  top: 34px;
  right: 34px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
  letter-spacing: .08em;
  color: var(--slate);
  border: 1px solid var(--line);
  border-radius: 2px;
  padding: 5px 10px;
}

.agm-eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: var(--accent);
  margin: 0 0 10px;
}

.agm-title {
  font-family: Georgia, "Iowan Old Style", "Times New Roman", serif;
  font-size: 34px;
  line-height: 1.15;
  font-weight: 400;
  letter-spacing: -.01em;
  margin: 0 0 18px;
}

.agm-meta {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  color: var(--slate);
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  padding: 12px 0;
  margin: 0 0 28px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
}
.agm-meta span { white-space: nowrap; }

.agm-actions { display: flex; flex-wrap: wrap; gap: 12px; }

.agm-btn {
  font: inherit;
  font-size: 15px;
  font-weight: 500;
  padding: 12px 22px;
  border-radius: 2px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background-color .15s ease, color .15s ease, border-color .15s ease;
}
.agm-btn:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

.agm-btn--primary { background: var(--ink); color: #fff; }
.agm-btn--primary:hover { background: #24384A; }

.agm-btn--ghost { background: transparent; color: var(--ink); border-color: var(--line); }
.agm-btn--ghost:hover { border-color: var(--slate); background: #F4F7F8; }

.agm-note {
  margin: 20px 0 0;
  font-size: 13px;
  color: var(--slate);
}
.agm-note a { color: var(--accent); }

/* ---------- Viewer ---------- */
.agm-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(20, 33, 43, .82);
  display: flex;
  flex-direction: column;
}

.agm-bar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 18px;
  background: var(--ink);
  color: #fff;
}
.agm-bar-title {
  font-family: Georgia, "Iowan Old Style", "Times New Roman", serif;
  font-size: 17px;
  font-weight: 400;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.agm-bar-actions { display: flex; align-items: center; gap: 10px; flex: 0 0 auto; }

.agm-bar-link {
  color: #C7D3DA;
  font-size: 13px;
  text-decoration: none;
  border-bottom: 1px solid rgba(199, 211, 218, .4);
  padding-bottom: 1px;
}
.agm-bar-link:hover { color: #fff; }

.agm-close {
  font: inherit;
  font-size: 14px;
  padding: 8px 16px;
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, .35);
  border-radius: 2px;
  cursor: pointer;
}
.agm-close:hover { background: rgba(255, 255, 255, .12); }
.agm-close:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }

.agm-frame-wrap { flex: 1 1 auto; background: #55636D; min-height: 0; }
.agm-frame { width: 100%; height: 100%; border: 0; display: block; }

@media (max-width: 560px) {
  .agm-root { padding: 24px 14px; }
  .agm-card { padding: 30px 22px 26px; }
  .agm-title { font-size: 27px; }
  .agm-stamp { position: static; display: inline-block; margin-bottom: 14px; }
  .agm-actions .agm-btn { flex: 1 1 100%; text-align: center; }
}

@media (prefers-reduced-motion: reduce) {
  .agm-btn { transition: none; }
}
`;

const PDFViewer1 = () => {
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const openViewer = () => setIsViewerOpen(true);
  const closeViewer = useCallback(() => setIsViewerOpen(false), []);

  const handleDownload = () => {
    const link = document.createElement('a');

    link.href = pdfFile;
    link.download = DOWNLOAD_NAME;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Close on Escape and stop the page behind from scrolling.
  useEffect(() => {
    if (!isViewerOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeViewer();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isViewerOpen, closeViewer]);

  return (
    <div className="agm-root">
      <style>{styles}</style>

      <div className="agm-card">
        <div className="agm-stamp">{DOC_YEAR}</div>

        <p className="agm-eyebrow">{DOC_EYEBROW}</p>
        <h1 className="agm-title">{DOC_TITLE}</h1>

        <div className="agm-meta">
          <span>PDF</span>
          <span>Financial year {DOC_YEAR}</span>
          <span>{DOWNLOAD_NAME}</span>
        </div>

        <div className="agm-actions">
          <button
            type="button"
            className="agm-btn agm-btn--primary"
            onClick={openViewer}
          >
            View document
          </button>

          <button
            type="button"
            className="agm-btn agm-btn--ghost"
            onClick={handleDownload}
          >
            Download
          </button>
        </div>

        <p className="agm-note">
          Opens in a full-screen reader. You can also{' '}
          <a href={pdfFile} target="_blank" rel="noopener noreferrer">
            open it in a new tab
          </a>
          .
        </p>
      </div>

      {isViewerOpen && (
        <div className="agm-overlay" role="dialog" aria-modal="true" aria-label={DOC_TITLE}>
          <div className="agm-bar">
            <h2 className="agm-bar-title">
              {DOC_TITLE} &middot; {DOC_YEAR}
            </h2>

            <div className="agm-bar-actions">
              <a
                className="agm-bar-link"
                href={pdfFile}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in new tab
              </a>

              <button type="button" className="agm-close" onClick={closeViewer}>
                Close
              </button>
            </div>
          </div>

          <div className="agm-frame-wrap">
            <iframe
              src={`${pdfFile}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`}
              className="agm-frame"
              title={DOC_TITLE}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFViewer1;